import { useState, useEffect, useRef } from "react";
import { getMinDistanceKadToClient } from "./utils";
import { GLOBAL_CONSTANTS } from '../../constants';
import { useOptions } from '../../OptionsContext';
import { getSelectedComparisonKey, isSelectedComparisonPresent } from './gazSelectedComparison';
import { getComparisonRequestRows } from './gazComparisonRows';
import { isSameNumericValue } from '../../utils/sameNumericValue';
import { scrollAppIntoView } from '../../utils/pageScroll';
import { getNomenclatureImageTarget } from '../../utils/nomenclatureImageTarget';
import { isVtoroySortName } from '../Offer/gazVtoroySort';

const IZOLIFE_SOURCE_PREFIX = 'ПРАЙС ИЗОЛАЙФ';
const isIzolifeSource = (source) =>
  source && String(source).toUpperCase().includes(IZOLIFE_SOURCE_PREFIX.toUpperCase());

const GAZ_DELIVERY_CATEGORY_NAMES = new Set([
  'бордюр',
  'бордюр дорожный',
  'брусчатка',
  'брусчатка и тротуарная плитка',
  'плитка тротуарная',
  'газонная решетка',
  'лоток водоотводный жби',
  'лоток водоотводный тротуарный',
]);

const isGazDeliveryCategory = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return normalized.includes('газобетон') || GAZ_DELIVERY_CATEGORY_NAMES.has(normalized);
};

const extractRegionFromIzolifeSource = (source) => {
  const upper = String(source || '').toUpperCase();
  if (upper.includes('СПБ')) return 'СПБ';
  if (upper.includes('МСК')) return 'МСК';
  if (upper.includes('КРД')) return 'КРД';
  return null;
};

const parseNumIzo = (v) => {
  const n = parseFloat(String(v || '0').replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

// Общестрой: сначала пытаемся показать только производства со свежей ценой (< 14 дней);
// если во всём списке сравнения таких нет — показываем как обычные все, что младше 60 дней
// (эта верхняя граница уже обеспечена backend'ом: цена старше 60 дней приходит нулевой).
const OBSHCHESTROY_FRESH_DAYS_THRESHOLD = 14;

const getPriceDaysOld = (dateStr) => {
  if (!dateStr || dateStr === "Нет данных" || dateStr === "—") return null;
  const days = Math.floor(
    (new Date() - new Date(dateStr.split('.').reverse().join('-'))) / (1000 * 60 * 60 * 24)
  );
  return Number.isFinite(days) ? days : null;
};

// Расчет стоимости доставки для ООО Изолайф
// Возвращает { totalMaterialCost, izolifeDeliveryType, izolifePalletPrice, izolifeComment, izolifePhrase, adjustedMaterials }
const calcIzolifeEntryDelivery = (materials, izolifeSettings) => {
  let totalMaterialCost = 0;
  let izolifeDeliveryType = 'full_trucks';
  let izolifePalletPrice = 0;
  let izolifeComment = null;
  let izolifePerPalletsComment = null;
  let izolifePalletsComment = null;
  let izolifePhrase = null;
  const adjustedMaterials = [];

  for (const mat of materials) {
    if (!isIzolifeSource(mat.source)) {
      totalMaterialCost += Math.ceil(mat.pricePerOne * mat.quantity);
      adjustedMaterials.push(mat);
      continue;
    }

    const region = extractRegionFromIzolifeSource(mat.source);
    const allSettings = izolifeSettings.settings || [];
    const izoSettings = allSettings.find(s => s.REGION === region) || {};
    const palletPrice = parseNumIzo(izoSettings.PALLET_PRICE);
    const phrase = izoSettings.PHRASE || '';

    const deliveryCostData = (izolifeSettings.deliveryCostByRegion || {})[region] || {};
    const costPerTrip = parseNumIzo(deliveryCostData.PODACHA);

    const numberPerPallet = mat.unitsPerPallet || 1;
    const truckPallets = parseNumIzo((izolifeSettings.truckPalletsByProduct || {})[mat.name]) || 0;

    const requiredPackages = mat.quantity;
    const priceValue = mat.pricePerOne;

    // 1. Сколько нужно паллет для требуемого количества упаковок
    const requiredPallets = Math.ceil(requiredPackages / numberPerPallet);

    // 2. Сколько всего паллет будет с учетом правил округления
    let effectivePallets;

    if (palletPrice) {
      // Если есть palletPrice - округляем до целых поддонов,
      // но для полного количества фур доставка уже считается включенной в материалы.
      effectivePallets = requiredPallets;
      const hasFullTruckLoad = truckPallets > 0 && requiredPallets > 0 && requiredPallets % truckPallets === 0;
      if (hasFullTruckLoad) {
        izolifeDeliveryType = 'full_trucks';
        izolifePalletPrice = 0;
      } else {
        izolifeDeliveryType = 'partial_truck';
        izolifePalletPrice = palletPrice;
      }
    } else if (truckPallets > 0) {
      // Если нет palletPrice, но есть truckPallets - округляем до полной фуры
      const requiredTrucks = Math.ceil(requiredPallets / truckPallets);
      effectivePallets = requiredTrucks * truckPallets;
      izolifeDeliveryType = 'full_trucks';
      izolifePalletPrice = 0;
    } else {
      // Если нет ни palletPrice, ни truckPallets - берем как есть
      effectivePallets = requiredPallets;
      izolifeDeliveryType = 'no_constraints';
      izolifePalletPrice = 0;
    }

    // 3. Сколько всего упаковок будет
    const actualPackages = effectivePallets * numberPerPallet;

    // 4. Комментарий, если берется больше, чем нужно
    if (requiredPackages !== actualPackages) {
      izolifeComment = `Нужно ${requiredPackages} упаковок, но до полной погрузки берется ${actualPackages}.`;
      izolifePerPalletsComment = `Количество на поддоне ${numberPerPallet}`;
      izolifePalletsComment = `Количество поддонов ${effectivePallets}`;
    } else {
      izolifeComment = null;
      izolifePerPalletsComment = null;
      izolifePalletsComment = null;
    }

    // 5. Фраза из настроек
    if (phrase && !izolifePhrase) {
      izolifePhrase = phrase;
    }

    // 6. Общая стоимость (цена * фактическое количество упаковок)
    totalMaterialCost += priceValue * actualPackages;

    // 7. Добавляем скорректированный материал
    adjustedMaterials.push({
      ...mat,
      pricePerOne: priceValue,
      quantity: actualPackages,
    });
  }

  return {
    totalMaterialCost,
    izolifeDeliveryType,
    izolifePalletPrice,
    izolifeComment,
    izolifePerPalletsComment,
    izolifePalletsComment,
    izolifePhrase,
    adjustedMaterials
  };
};

// Функция для определения категории калькулятора (возвращает полное название)
const getCalculatorCategory = (calculatorName) => {
  if (!calculatorName) return 'Общестрой'; // по умолчанию Общестрой

  // Прямое сопоставление с названиями из GLOBAL_CONSTANTS.CATEGORY_NAMES
  const categoryValues = Object.values(GLOBAL_CONSTANTS.CATEGORY_NAMES);
  const matchedCategory = categoryValues.find(value =>
    value.trim().toLowerCase() === calculatorName.trim().toLowerCase()
  );

  return matchedCategory || 'Общестрой';
};

// Нормы поддонов приходят из двух мест с разным типом: в справочнике номенклатуры
// они лежат строкой, в расчёте сравнения считаются числом. Для проверки «изменилось
// ли» важно значение, а не запись.
const isSameNormValue = isSameNumericValue;

const useProductionsLogic = (
  products,
  selfPickUp,
  comparisons,
  setComparisons,
  setSelectedOffer,
  offerRef,
  izolifeSettings,
  setProducts,
  isMoscowRegion,
  setShowScrollButton,
  isMskGazobetonScope = false
) => {
  const [isLoading, setIsLoading] = useState(false);
  const { setOptionsByPayment } = useOptions();
  const manuallySelectedRef = useRef(false);
  // Производство, по которому собрано текущее КП. Хранится ключом сравнения
  // (`comparison.id` — это имя производителя, см. BackendProductions), а не `entry.id`:
  // в `entry.id` входит позиция сравнения в исходном массиве, поэтому после пересчёта
  // тот же id мог указать на другую строку. Ключ сравнения от позиции и сортировки
  // не зависит и уникален: сравнения строятся по `Object.entries` номенклатуры,
  // то есть по одному на производителя.
  //
  // Пишется здесь, потому что через handleSelectOffer проходят оба пути выбора —
  // автоматический и ручной. Значение только фиксируется; на расчёт и на выбор
  // оффера не влияет.
  const [selectedComparisonId, setSelectedComparisonId] = useState(null);

  // Вторая проверка того же ввода: она гасит таблицу сравнения и тоже обнуляет
  // comparisons. Внутри МСК Газобетона незаполненная строка «Товаров» (нажали
  // «Добавить товар», номенклатуру ещё не выбрали) не считается позицией заказа
  // и не должна прятать уже посчитанные аналоги. Вне области список приходит
  // тем же массивом — проверка остаётся базовой.
  const rowsForValidation = getComparisonRequestRows({ products, isMskGazobetonScope });

  const isValidInput =
    rowsForValidation &&
    rowsForValidation.length > 0 &&
    rowsForValidation.every((product) => product.name); // Проверяем только наличие имени

  const hasValidDescriptions = comparisons.some((comparison) =>
    comparison.materials.some(
      (material) =>
        material.description &&
        material.description !== "Нет данных" &&
        material.description !== "-"
    )
  );

  useEffect(() => {
    // Игнорируем временные изменения (с флагом _isTemporary)
    const hasTemporaryChanges = products.some(product => product._isTemporary);
    if (hasTemporaryChanges) {
      return;
    }

    console.log("🔍 useProductionsLogic: isValidInput=", isValidInput, "products=", products);
    if (!isValidInput) {
      console.log("🔍 Сбрасываем comparisons из-за невалидного ввода");
      setIsLoading(false);
      setComparisons([]);
      setShowScrollButton(false);
    }
  }, [products, setComparisons]);

  const isGazMaterial = (material) => {
    const calculator = String(material?.calculator || '').toLowerCase();
    const category = String(material?.category || '').toLowerCase();
    return isGazDeliveryCategory(calculator) || isGazDeliveryCategory(category);
  };

  // Третий аргумент помечает выбор ручным, четвёртый — уводить ли экран к расчёту
  // заказа. По умолчанию они совпадают, поэтому клик по строке сравнения работает
  // как раньше: помечает выбор и поднимает к расчёту. Разделены они ради двух мест
  // внутри МСК Газобетона, где выбор ручной, а прокрутка мешает: повторное
  // применение уже выбранного производства при пересчёте и кнопки выбора в сводке.
  // Там экран и так стоит на расчёте, и прыжок сбивал менеджера.
  const handleSelectOffer = (comparison, entry, markManual = false, scrollToOffer = markManual) => {
    setSelectedComparisonId(getSelectedComparisonKey(comparison, entry));
    if (markManual) {
      manuallySelectedRef.current = true;
    }
    const selectedMaterials = entry.materialsToShow;
    const isIzolifeEntry = Boolean(entry?.isIzolifeEntry);
    const isIzolifePartialTruck = isIzolifeEntry && entry.izolifeDeliveryType === 'partial_truck';
    const isIzolifeFullTrucks = isIzolifeEntry && entry.izolifeDeliveryType === 'full_trucks';

    const formattedMaterials = selectedMaterials.map((material) => {
      let pricePerOne = material.pricePerOne || 0;
      let quantity = material.quantity || 1;
      let sum = Math.ceil(material.pricePerOne * quantity) || 0;

      if (material.calculator === "Нерудные материалы" || material.calculator === "Бетон") {
        sum = entry.totalSum || 0;
        pricePerOne = quantity > 0 ? Math.ceil(sum / quantity) : 0;
      }

      return {
        name: material.name || "Не указано",
        pricePerOne,
        quantity,
        sum,
        date: material.date || "—",
        unit: material.unit || "шт",
        bitrixId: material.bitrixId || "",
        calculator: material.calculator || "",
        description: material.description || "—",
        source: material.source || "-",
        massPerOne: material.massPerOne || 0,
        volumePerOne: material.volumePerOne || 0,
        manufacturer: material.manufacturer || "",
        category: material.category || "",
        calculatorCategory: getCalculatorCategory(material.calculator), // Добавляем правильную категорию калькулятора
        factVolume: material.factVolume ?? "-",
        numberPallets: material.numberPallets || 1,
        palletUnits: material.palletUnits || 1,
        totalPallets: material.totalPallets ?? "-",
        occupiedPallets: material.occupiedPallets ?? "-",

        izolifeComment: entry.izolifeComment || null,
        izolifePalletsComment: entry.izolifePalletsComment || null,
        izolifePerPalletsComment: entry.izolifePerPalletsComment || null,
        izolifePhrase: entry.izolifePhrase || null,
        izolifeDeliveryType: entry.izolifeDeliveryType || null,
        izolifePalletPrice: entry.izolifePalletPrice || 0,
      };
    });

    let deliveryData = {};
    if (selfPickUp) {
      deliveryData = { bestMethod: "Самовывоз", numberOfTrips: 0, costPerTrip: 0, totalCost: 0 };
    } else if (isIzolifeEntry) {
      if (isIzolifePartialTruck) {
        const palletPrice = Number(entry.izolifePalletPrice || 0);
        deliveryData = {
          bestMethod: "Доставка",
          numberOfTrips: 1,
          costPerTrip: palletPrice,
          totalCost: palletPrice,
          bitrixId: "53973",
        };
      } else {
        // Полная фура: доставка включена в цену материалов, строку доставки не показываем
        deliveryData = { bestMethod: "Самовывоз", numberOfTrips: 0, costPerTrip: 0, totalCost: 0 };
      }
    } else {
      const selectedCategories = entry.categoriesToInclude;
      if (selectedCategories.includes("jbi")) {
        const jbiDetails = comparison.delivery?.jbi?.details;
        if (jbiDetails && typeof jbiDetails === "object") {
          if ("error" in jbiDetails) {
            console.error("❌ Ошибка в jbiDetails:", jbiDetails.error);
            deliveryData = { bestMethod: "Самовывоз", numberOfTrips: 0, costPerTrip: 0, totalCost: 0 };
          } else if ("data" in jbiDetails) {
            deliveryData = jbiDetails.data || {
              bestMethod: "Самовывоз",
              numberOfTrips: 0,
              costPerTrip: 0,
              totalCost: 0,
            };
          } else if (Array.isArray(jbiDetails)) {
            const deliveryGroup = jbiDetails.find((group) => group.groupKey === "all");
            deliveryData = deliveryGroup?.deliveryData?.data || {
              bestMethod: "Самовывоз",
              numberOfTrips: 0,
              costPerTrip: 0,
              totalCost: 0,
            };
          } else {
            console.error("❌ Неверный формат jbiDetails:", jbiDetails);
            deliveryData = { bestMethod: "Самовывоз", numberOfTrips: 0, costPerTrip: 0, totalCost: 0 };
          }
        } else {
          console.error("❌ jbiDetails не является объектом:", jbiDetails);
          deliveryData = { bestMethod: "Самовывоз", numberOfTrips: 0, costPerTrip: 0, totalCost: 0 };
        }
      } else {
        // Используем calculator из первого материала для определения категории
        const materialCategory = isGazDeliveryCategory(selectedMaterials[0].calculator) ||
          isGazDeliveryCategory(selectedMaterials[0].category) ? "gaz" :
          selectedMaterials[0].calculator === "Пиломатериалы" ? "pilmat" :
            selectedMaterials[0].calculator === "Металлочерепица, профлист и штакетник" ? "grandline" :
              selectedMaterials[0].calculator === "Металл" ? "metall" :
                selectedCategories[0];
        const categoryDelivery = comparison.delivery?.[materialCategory]?.details?.data || {};
        console.log(`🔍 useProductionsLogic: materialCategory=${materialCategory}, categoryDelivery=`, categoryDelivery);
        const isGazCategory = materialCategory === "gaz";
        deliveryData = {
          bestMethod: categoryDelivery.bestMethod || "Самовывоз",
          numberOfTrips: categoryDelivery.numberOfTrips || 0,
          costPerTrip: categoryDelivery.costPerTrip || 0,
          totalCost: categoryDelivery.totalCost || 0,
          bitrixId: categoryDelivery.bitrixId || "",
          ...(isGazCategory
            ? {
              deliveryBreakdown: categoryDelivery.deliveryBreakdown || null,
              calculationMode: categoryDelivery.calculationMode || null,
              usedCentralized: Boolean(categoryDelivery.usedCentralized),
              // Объект собирается заново, поле за полем, поэтому плечо нужно
              // переносить здесь: ниже в состояние заказа попадает уже он, а не
              // ответ сервера, и без этой строки километраж теряется.
              distanceProdToClient: categoryDelivery.distanceProdToClient ?? null,
              distanceKadToClient: categoryDelivery.distanceKadToClient ?? null,
            }
            : {}),
        };
      }
    }

    const isGazSelectedOffer = formattedMaterials.some((material) => {
      const calculator = String(material?.calculatorCategory || material?.calculator || '').toLowerCase();
      const category = String(material?.category || '').toLowerCase();
      return isGazDeliveryCategory(calculator) || isGazDeliveryCategory(category);
    });

    const selectedOfferData = {
      production: comparison.production || comparison.id,
      // Куда смотреть за фото завода — считается здесь, потому что только здесь
      // ещё видна строка сравнения целиком. Дальше в расчёт уходят уже
      // переформатированные материалы, в которых ни ID производства, ни guide_id
      // не остаётся. Кладётся только для газобетона: вне области сводка фото не
      // показывает, и лишнее поле в состоянии там ни к чему.
      ...(isGazSelectedOffer
        ? { nomenclatureImage: getNomenclatureImageTarget(entry) }
        : {}),
      // Регион, по которому считалась доставка производства — нужен, чтобы
      // альтернативные способы доставки в КП считались по ставкам того же региона
      region: comparison.region || null,
      materials: formattedMaterials,
      ...(deliveryData
        ? {
          delivery: {
            name: deliveryData.bestMethod || "Самовывоз",
            numberOfTrips: deliveryData.numberOfTrips || 0,
            costPerTrip: deliveryData.costPerTrip || 0,
            totalCost: deliveryData.totalCost || 0,
            bitrixId: deliveryData.bitrixId || "",
            ...(isGazSelectedOffer
              ? {
                deliveryBreakdown: deliveryData.deliveryBreakdown || null,
                calculationMode: deliveryData.calculationMode || null,
                usedCentralized: Boolean(deliveryData.usedCentralized),
                // Плечо от завода до адреса сервер считает и отдаёт вместе с
                // рейсами. Раньше оно сюда не доходило, и сводка не могла
                // показать километраж рядом с суммой доставки.
                distanceProdToClient: deliveryData.distanceProdToClient ?? null,
                distanceKadToClient: deliveryData.distanceKadToClient ?? null,
              }
              : {}),
          },
        }
        : {}),
      deliveryLocked: isIzolifeEntry,
      deliveryIncludedInMaterials: isIzolifeFullTrucks,
      date: formattedMaterials.length > 0 ? formattedMaterials[0].date : "",
    };

    console.log("📦 selectedOfferData:", selectedOfferData);

    setSelectedOffer(selectedOfferData);

    // Обновляем нормы погрузки в optionsByPayment для выбранного производства,
    // чтобы блок «Товары» пересчитал поддоны и штуки по нормам этого производства.
    // Products.js читает pallet-данные из optionsByPayment (не из root options).
    if (setOptionsByPayment && selectedMaterials.length > 0) {
      const normalizeKey = (v) =>
        String(v || "").trim().toLowerCase().replace(/ё/g, "е").replace(/х/g, "x").replace(/\s+/g, " ");

      const applyNormsToCategory = (categoryMap) => {
        if (!categoryMap) return categoryMap;
        let changed = false;
        let updated = categoryMap;

        for (const mat of selectedMaterials) {
          if (!mat.palletUnits || mat.palletUnits <= 0) continue;
          const productName = mat.requestedName || mat.name;
          if (!productName) continue;

          const normalizedName = normalizeKey(productName);
          const matchedKey = updated[productName] != null
            ? productName
            : Object.keys(updated).find(k => normalizeKey(k) === normalizedName);

          if (!matchedKey) continue;

          const existing = updated[matchedKey];

          // Проверяем, реально ли изменятся значения, чтобы не создавать новый объект впустую.
          // Сравнение по значению: из справочника нормы приходят строками («1.44»),
          // из расчёта сравнения — числами. Строгое сравнение считало их разными
          // всегда, поэтому options пересоздавались на каждый пересчёт, а за ними
          // пересобирались строки товаров и уходил новый круг запросов.
          const willChangePallet =
            !isSameNormValue(existing?.PALLET_UNITS, mat.palletUnits) ||
            !isSameNormValue(existing?.pallet_per_unit, mat.palletUnits);
          const willChangeUnits =
            mat.unitsPerPallet != null && mat.unitsPerPallet > 0 && (
              !isSameNormValue(existing?.number_per_pallet, mat.unitsPerPallet) ||
              !isSameNormValue(existing?.UNITS_PER_PALLET, mat.unitsPerPallet)
            );

          if (!willChangePallet && !willChangeUnits) continue;

          if (!changed) {
            updated = { ...categoryMap };
            changed = true;
          }

          const palletUpdates = {
            PALLET_UNITS: mat.palletUnits,
            pallet_per_unit: mat.palletUnits,
          };
          if (mat.unitsPerPallet != null && mat.unitsPerPallet > 0) {
            palletUpdates.number_per_pallet = mat.unitsPerPallet;
            palletUpdates.UNITS_PER_PALLET = mat.unitsPerPallet;
            palletUpdates.NUMBER_PER_PALLET = mat.unitsPerPallet;
          }

          updated[matchedKey] = { ...existing, ...palletUpdates };
        }

        return changed ? updated : categoryMap;
      };

      setOptionsByPayment(prev => {
        if (!prev) return prev;
        const newCash = applyNormsToCategory(prev.cash);
        const newNoncash = applyNormsToCategory(prev.noncash);
        const newDefault = applyNormsToCategory(prev.default);
        const changed = newCash !== prev.cash || newNoncash !== prev.noncash || newDefault !== prev.default;
        return changed ? { cash: newCash, noncash: newNoncash, default: newDefault } : prev;
      });
    }

    if (scrollToOffer) {
      setShowScrollButton(false);
      setTimeout(() => {
        const target = offerRef?.current || document.querySelector('.rd-kp-section');
        if (target) {
          scrollAppIntoView(target);
        }
      }, 300);
    } else {
      setShowScrollButton(true);
    }
  };

  const groupedData = comparisons.reduce((acc, comparison, comparisonIndex) => {
    const jbiMaterials = comparison.materials.filter((material) => material.calculator === "ЖБИ");
    const otherMaterials = comparison.materials.filter((material) => material.calculator !== "ЖБИ");

    if (jbiMaterials.length > 0) {
      const allUniqueDescriptions = [...new Set(jbiMaterials.map(m => m.description))];

      const productMap = products.reduce((map, product, index) => {
        map[product.name] = { quantity: product.quantity, index };
        return map;
      }, {});

      if (allUniqueDescriptions.length === 1 || allUniqueDescriptions.every(desc => desc === "Нет данных" || desc === "-")) {
        const materialsToShow = products.map((product) => {
          const materialsForName = jbiMaterials.filter(m => m.name === product.name);
          console.log(`🔍 materialsForName для ${product.name}:`, materialsForName);
          if (!materialsForName.length) {
            console.warn(`⚠️ Нет материалов для ${product.name}`);
            return null;
          }
          const material = materialsForName[0];
          return {
            ...material,
            quantity: product.quantity || 1,
          };
        }).filter(Boolean);

        if (materialsToShow.length === 0) {
          console.warn("⚠️ materialsToShow пуст для ЖБИ");
          return acc;
        }

        const totalMaterialCost = materialsToShow.reduce((sum, mat) => sum + mat.quantity * mat.pricePerOne, 0);

        let deliveryCostForGroup = 0;
        const deliveryGroups = [];
        if (!selfPickUp) {
          const jbiDetails = comparison.delivery?.jbi?.details;
          console.log("🔍 jbiDetails:", jbiDetails);
          if (jbiDetails && typeof jbiDetails === "object" && !("error" in jbiDetails)) {
            if ("data" in jbiDetails) {
              deliveryCostForGroup = jbiDetails.data?.totalCost || 0;
              deliveryGroups.push({ deliveryData: jbiDetails });
            } else if (Array.isArray(jbiDetails)) {
              jbiDetails.forEach((group) => {
                if (group?.deliveryData?.data?.totalCost) {
                  deliveryCostForGroup += group.deliveryData.data.totalCost || 0;
                  deliveryGroups.push(group);
                }
              });
            }
          } else {
            console.error("❌ Проблема с jbiDetails:", jbiDetails);
          }
        }

        const totalDeliveryCost = selfPickUp ? 0 : deliveryCostForGroup;
        const totalSum = totalMaterialCost + totalDeliveryCost;

        acc.push({
          comparison,
          material: materialsToShow[0],
          production: comparison.production,
          productionInfo: comparison.productionInfo,
          delivery: comparison.delivery,
          materialsToShow,
          allMaterials: jbiMaterials,
          totalMaterialCostForDisplay: totalMaterialCost,
          totalDeliveryCost,
          totalSum,
          id: `${comparison.id}-jbi-${comparisonIndex}`,
          deliveryForTooltip: { jbi: { category: "ЖБИ", details: deliveryGroups } },
          categoriesToInclude: ["jbi"],
        });
      } else {
        const generateCombinations = (items, count, currentCombo = [], index = 0) => {
          if (currentCombo.length === count) {
            return [currentCombo];
          }
          const combinations = [];
          for (let i = index; i < items.length; i++) {
            const newCombo = [...currentCombo, items[i]];
            combinations.push(...generateCombinations(items, count, newCombo, i + 1));
          }
          return combinations;
        };

        const totalJbiCount = products.length;
        const allCombinations = generateCombinations(allUniqueDescriptions, totalJbiCount);
        console.log("🔍 allCombinations:", allCombinations);

        allCombinations.forEach((combo, comboIndex) => {
          const materialsToShow = products.map((product, i) => {
            const materialsForName = jbiMaterials.filter(m => m.name === product.name);
            console.log(`🔍 materialsForName для ${product.name}:`, materialsForName);
            if (!materialsForName.length) {
              console.warn(`⚠️ Нет материалов для ${product.name}`);
              return null;
            }
            const description = combo[i];
            const material = materialsForName.find(m => m.description === description) || materialsForName.find(m => m.description) || materialsForName[0];
            return {
              ...material,
              quantity: product.quantity || 1,
            };
          }).filter(Boolean);

          if (materialsToShow.length === 0) {
            console.warn("⚠️ materialsToShow пуст для комбинации:", combo);
            return;
          }

          const totalMaterialCost = materialsToShow.reduce((sum, mat) => sum + mat.quantity * mat.pricePerOne, 0);

          let deliveryCostForGroup = 0;
          const deliveryGroups = [];
          if (!selfPickUp) {
            const jbiDetails = comparison.delivery?.jbi?.details;
            console.log("🔍 jbiDetails:", jbiDetails);
            if (jbiDetails && typeof jbiDetails === "object" && !("error" in jbiDetails)) {
              if ("data" in jbiDetails) {
                deliveryCostForGroup = jbiDetails.data?.totalCost || 0;
                deliveryGroups.push({ deliveryData: jbiDetails });
              } else if (Array.isArray(jbiDetails)) {
                jbiDetails.forEach((group) => {
                  if (group?.deliveryData?.data?.totalCost) {
                    deliveryCostForGroup += group.deliveryData.data.totalCost || 0;
                    deliveryGroups.push(group);
                  }
                });
              }
            } else {
              console.error("❌ Проблема с jbiDetails:", jbiDetails);
            }
          }

          const totalDeliveryCost = selfPickUp ? 0 : deliveryCostForGroup;
          const totalSum = totalMaterialCost + totalDeliveryCost;

          acc.push({
            comparison,
            material: materialsToShow[0],
            production: comparison.production,
            productionInfo: comparison.productionInfo,
            delivery: comparison.delivery,
            materialsToShow,
            allMaterials: jbiMaterials,
            totalMaterialCostForDisplay: totalMaterialCost,
            totalDeliveryCost,
            totalSum,
            id: `${comparison.id}-${comboIndex}-${comparisonIndex}-jbi`,
            deliveryForTooltip: { jbi: { category: "ЖБИ", details: deliveryGroups } },
            categoriesToInclude: ["jbi"],
          });
        });
      }
    }

    if (otherMaterials.length > 0) {
      const hasIzolife = otherMaterials.some(mat => isIzolifeSource(mat.source));

      if (hasIzolife && izolifeSettings && !selfPickUp) {
        const { totalMaterialCost, izolifeDeliveryType, izolifePalletPrice, izolifeComment, izolifePalletsComment, izolifePerPalletsComment, izolifePhrase, adjustedMaterials } =
          calcIzolifeEntryDelivery(otherMaterials, izolifeSettings);

        acc.push({
          comparison,
          material: otherMaterials[0],
          production: comparison.production,
          productionInfo: comparison.productionInfo,
          delivery: comparison.delivery,
          materialsToShow: adjustedMaterials,
          allMaterials: comparison.materials,
          totalMaterialCostForDisplay: totalMaterialCost,
          totalDeliveryCost: izolifeDeliveryType === 'partial_truck' ? izolifePalletPrice : 0,
          totalSum: totalMaterialCost + (izolifeDeliveryType === 'partial_truck' ? izolifePalletPrice : 0),
          id: `${comparison.id}-other-${comparisonIndex}`,
          deliveryForTooltip: comparison.delivery,
          categoriesToInclude: ["os", "gaz", "ner", "mix", "pilmat", "grandline", "metall", "other"],
          isIzolifeEntry: true,
          izolifeDeliveryType,
          izolifePalletPrice,
          izolifeComment,
          izolifePalletsComment,
          izolifePerPalletsComment,
          izolifePhrase,
        });
      } else {
        const deliveryCategories = ["os", "gaz", "ner", "mix", "pilmat", "grandline", "metall", "other"];
        const totalDeliveryCostForOther = selfPickUp
          ? 0
          : deliveryCategories.reduce((sum, category) => sum + (comparison.delivery?.[category]?.details?.data?.totalCost || 0), 0);
        const totalMaterialCostForOther = otherMaterials.reduce(
          (sum, mat) => sum + Math.ceil(mat.pricePerOne * mat.quantity),
          0
        );
        const totalSumForOther = totalMaterialCostForOther + totalDeliveryCostForOther;

        acc.push({
          comparison,
          material: otherMaterials[0],
          production: comparison.production,
          productionInfo: comparison.productionInfo,
          delivery: comparison.delivery,
          materialsToShow: otherMaterials,
          allMaterials: comparison.materials,
          totalMaterialCostForDisplay: totalMaterialCostForOther,
          totalDeliveryCost: totalDeliveryCostForOther,
          totalSum: totalSumForOther,
          id: `${comparison.id}-other-${comparisonIndex}`,
          deliveryForTooltip: comparison.delivery,
          categoriesToInclude: ["os", "gaz", "ner", "mix", "pilmat", "grandline", "metall", "other"],
        });
      }
    }

    return acc;
  }, []);

  // Уникализация groupedData
  const uniqueGroupedData = [];
  const seenKeys = new Set();
  groupedData.forEach(entry => {
    const key = `${entry.comparison?.id || entry.id}-${entry.materialsToShow
      .map((m) => `${m.name}-${m.description}-${m.quantity}-${m.source || "-"}-${m.pricePerOne || 0}`)
      .join("|")}`;
    // console.log(`🔑 Ключ для уникализации: ${key}`);
    if (!seenKeys.has(key)) {
      seenKeys.add(key);
      uniqueGroupedData.push(entry);
    } else {
      console.log(`🚫 Дубликат исключён: ${key}`);
    }
  });

  // Фильтрация
  const filteredGroupedData = uniqueGroupedData.filter((entry) => {
    if (entry.isIzolifeEntry) return true;

    if (!selfPickUp && entry.totalDeliveryCost === 0) {
      const hasGazMaterials = (entry.materialsToShow || []).some((material) =>
        isGazDeliveryCategory(material?.calculatorCategory || material?.calculator) ||
        isGazDeliveryCategory(material?.category)
      );

      if (hasGazMaterials) {
        const deliveryError =
          entry?.deliveryForTooltip?.gaz?.details?.error ||
          entry?.comparison?.delivery?.gaz?.details?.error ||
          entry?.deliveryForTooltip?.gaz?.details ||
          entry?.comparison?.delivery?.gaz?.details ||
          "Не удалось рассчитать доставку";

        console.log(`⚠️ Оставляем ${entry.production} в списке: ошибка доставки газобетона ->`, deliveryError);
        entry.deliveryError = typeof deliveryError === "string" ? deliveryError : "Не удалось рассчитать доставку";
        entry.hasDeliveryError = true;
        entry.totalSum = entry.totalMaterialCostForDisplay;
        return true;
      }

      console.log(`⏩ Пропускаем ${entry.production}: totalDeliveryCost = 0 при !selfPickUp`);
      return false;
    }
    return true;
  });

  // Общестрой: если хотя бы у одного производства во всём списке сравнения цена свежее 14 дней,
  // остальные (>= 14 дней) в этом списке уходят в "регламент", даже если их цена ещё не обнулена backend'ом (< 60 дней).
  // Если свежих (< 14 дней) производств нет вообще — используем как обычные все, что backend не обнулил (< 60 дней).
  const hasFreshObshchestroy = filteredGroupedData.some(entry =>
    entry.materialsToShow && entry.materialsToShow.some(m => {
      if (m.calculator !== "Общестрой" || m.pricePerOne === 0) return false;
      const days = getPriceDaysOld(m.date);
      return days !== null && days < OBSHCHESTROY_FRESH_DAYS_THRESHOLD;
    })
  );

  // Функция для проверки, является ли запись "регламент"
  const isReagentEntry = (entry) => {
    const result = entry.materialsToShow && entry.materialsToShow.some(m => {
      if (m.calculator === "Общестрой") {
        // Цена уже обнулена backend'ом (старше 60 дней) — регламент безусловно
        if (m.pricePerOne === 0) return true;
        // Если в списке сравнения есть более свежие (< 14 дней) производства, эта запись (>= 14 дней) уходит в регламент
        if (hasFreshObshchestroy) {
          const days = getPriceDaysOld(m.date);
          if (days === null || days >= OBSHCHESTROY_FRESH_DAYS_THRESHOLD) return true;
        }
        return false;
      }
      // ЖБИ: регламент если цена изначально 0, или если клиент МСК и цена устарела
      if (m.calculator === "ЖБИ" && (m.pricePerOne === 0 || (m.isOldJbi && isMoscowRegion))) return true;
      return false;
    });
    console.log('[isReagentEntry] Entry:', entry.comparison?.production, 'Materials:', entry.materialsToShow?.map(m => ({ name: m.name, pricePerOne: m.pricePerOne, calculator: m.calculator, isOldJbi: m.isOldJbi, date: m.date })), 'isMoscowRegion:', isMoscowRegion, 'hasFreshObshchestroy:', hasFreshObshchestroy, 'isReagent:', result);
    return result;
  };

  const sortedGroupedData =
    filteredGroupedData[0]?.material?.calculator && filteredGroupedData[0].material.calculator === "Нерудные материалы"
      ? [...filteredGroupedData]
        .sort((a, b) => {
          // Сначала сортируем по наличию статуса "регламент" (обычные записи идут первыми)
          const aIsReagent = isReagentEntry(a);
          const bIsReagent = isReagentEntry(b);

          if (aIsReagent && !bIsReagent) return 1;  // "регламент" в конец
          if (!aIsReagent && bIsReagent) return -1; // обычные записи первыми

          // Если оба статуса одинаковые, сортируем по наличию цены (товары с ценой первыми)
          const aHasPrice = a.totalMaterialCostForDisplay > 0;
          const bHasPrice = b.totalMaterialCostForDisplay > 0;

          if (aHasPrice && !bHasPrice) return -1;  // товары с ценой первыми
          if (!aHasPrice && bHasPrice) return 1;   // товары "по запросу" в конец

          // Если оба статуса одинаковые, сортируем по общей сумме
          return a.totalSum - b.totalSum;
        })
        .slice(0, 10)
        .map(entry => ({
          ...entry,
          isReagent: isReagentEntry(entry) // Добавляем флаг для отображения "регламент"
        }))
      : [...filteredGroupedData]
        .sort((a, b) => {
          // Сначала сортируем по наличию статуса "регламент" (обычные записи идут первыми)
          const aIsReagent = isReagentEntry(a);
          const bIsReagent = isReagentEntry(b);

          if (aIsReagent && !bIsReagent) return 1;  // "регламент" в конец
          if (!aIsReagent && bIsReagent) return -1; // обычные записи первыми

          // Если оба статуса одинаковые, сортируем по наличию цены (товары с ценой первыми)
          const aHasPrice = a.totalMaterialCostForDisplay > 0;
          const bHasPrice = b.totalMaterialCostForDisplay > 0;

          if (aHasPrice && !bHasPrice) return -1;  // товары с ценой первыми
          if (!aHasPrice && bHasPrice) return 1;   // товары "по запросу" в конец

          // Если оба статуса одинаковые, сортируем по общей сумме
          return a.totalSum - b.totalSum;
        })
        .map(entry => ({
          ...entry,
          isReagent: isReagentEntry(entry) // Добавляем флаг для отображения "регламент"
        }));

  console.log('[sortedGroupedData] Итоговый порядок:', sortedGroupedData.map(e => ({
    production: e.comparison?.production,
    isReagent: e.isReagent,
    totalSum: e.totalSum
  })));

  // Всегда возвращаем отсортированные данные (включая записи "регламент")
  const finalGroupedData = sortedGroupedData;

  // Выбранное производство пропало из пересчитанных сравнений (кончилась цена, ушло
  // в ошибку доставки, перестало проходить фильтры). Отмечать вместо него соседнюю
  // строку нельзя, а оставлять КП от исчезнувшего производства — тем более. Снимаем
  // признак ручного выбора, и следующий за этим существующий автовыбор ниже сам
  // возьмёт дешевейший вариант — тот же путь, что и при первой загрузке сравнения.
  //
  // Эффект объявлен раньше автовыбора намеренно: в одном и том же коммите React
  // выполняет эффекты в порядке объявления, поэтому автовыбор увидит уже снятый флаг.
  //
  // Работает для всех регионов и калькуляторов. Раньше было закрыто признаком МСК
  // Газобетона, но «Расчёт заказа» от исчезнувшего производства — беда общая, и
  // состояние выбора (`selectedComparisonId`, `manuallySelectedRef`) пишется в
  // handleSelectOffer одинаково для всех калькуляторов, без привязки к области.
  useEffect(() => {
    if (!manuallySelectedRef.current) return;
    if (selectedComparisonId == null) return;
    if (!comparisons || comparisons.length === 0) return;

    if (!isSelectedComparisonPresent(comparisons, selectedComparisonId)) {
      manuallySelectedRef.current = false;
    }
  }, [comparisons, selectedComparisonId]);

  // Пересчёт сравнения обновляет и сам расчёт заказа, даже когда производство
  // выбрано вручную.
  //
  // Автовыбор ниже намеренно не трогает ручной выбор, и из-за этого расчёт наверху
  // оставался собранным по прежним данным: после переключения на безналичный расчёт
  // сравнение показывало новые цены, а «Расчёт заказа» — старые наличные. То же
  // самое происходило при смене адреса и количества.
  //
  // Выбор при этом не меняется: заново применяется то же самое производство по его
  // ключу. Дешевейшее не подставляется, ключ выбранного сравнения прежний.
  //
  // Область здесь тоже снята: пересобирать расчёт после пересчёта сравнения нужно
  // в любом калькуляторе, иначе КП остаётся спрятанным (App.js прячет его на смене
  // ввода) или, до той правки, показывало прежние числа.

  // Свежие строки и обработчик держим в ссылке: обе величины пересоздаются на каждый
  // рендер, и если внести их в зависимости эффекта ниже, он будет срабатывать вхолостую.
  const latestSelectionRef = useRef({ rows: finalGroupedData, selectOffer: handleSelectOffer });
  useEffect(() => {
    latestSelectionRef.current = { rows: finalGroupedData, selectOffer: handleSelectOffer };
  });

  // Ссылка на уже применённый массив сравнений держит эффект от повторного
  // срабатывания: handleSelectOffer меняет состояние и вызывает новый рендер, а
  // пересобирать расчёт нужно только на действительно новых данных.
  const appliedComparisonsRef = useRef(null);
  useEffect(() => {
    if (!manuallySelectedRef.current) return;
    if (selectedComparisonId == null) return;
    if (!Array.isArray(comparisons) || comparisons.length === 0) return;
    if (appliedComparisonsRef.current === comparisons) return;

    const { rows, selectOffer } = latestSelectionRef.current;
    const selectedEntry = (Array.isArray(rows) ? rows : []).find(
      (entry) => entry?.comparison?.id === selectedComparisonId
    );
    // Выбранного производства в новых данных нет — вмешиваться нельзя: эффект выше
    // уже снял признак ручного выбора, дальше сработает обычный автовыбор.
    if (!selectedEntry) return;

    appliedComparisonsRef.current = comparisons;
    // Прокрутки здесь быть не должно: эффект срабатывает сам, на пересчёте после
    // добавления строки товара или смены адреса, а не по действию менеджера.
    // Именно из-за неё «Добавить товар» уводило экран вниз на расчёт заказа.
    selectOffer(selectedEntry.comparison, selectedEntry, true, false);
  }, [comparisons, selectedComparisonId]);

  // Автоматически выбираем дешевейшее производство при загрузке сравнения.
  // Не перекрываем выбор, который пользователь сделал вручную.
  useEffect(() => {
    if (!comparisons || comparisons.length === 0) return;
    if (manuallySelectedRef.current) return;

    const validEntries = finalGroupedData.filter(e => !e.isReagent && e.totalMaterialCostForDisplay > 0);
    const gazobetonyProducts = (products || []).filter(p => String(p.name || '').toLowerCase().includes('газобетон'));

    // Второй сорт в автоподбор не идёт, пока его не запросили: он дешевле обычного
    // блока всегда, и как «самый дешёвый вариант» подменял бы собой запрошенный
    // товар. Менеджер искал первый сорт, а в расчёте оказывался второй
    // (20.10.2026, Роман). В таблице сравнения второй сорт при этом остаётся —
    // отдельной группой внизу, выбрать его можно вручную.
    const zaproshenVtoroySort = (products || []).some((p) => isVtoroySortName(p?.name));
    const bezVtorogoSorta = (spisok) => spisok.filter((e) => !(
      (e.materialsToShow || []).some((m) => isVtoroySortName(m?.name))
    ));
    const podhodyashchie = zaproshenVtoroySort ? validEntries : bezVtorogoSorta(validEntries);
    // Если первосортных предложений нет вовсе, выбирать всё равно нужно — иначе
    // расчёт не соберётся. Тогда берётся то, что есть, вместе со вторым сортом.
    const dlyaVybora = podhodyashchie.length > 0 ? podhodyashchie : validEntries;

    let cheapestEntry;
    if (isMskGazobetonScope) {
      // Внутри МСК Газобетона первым всегда встаёт самый дешёвый вариант — список
      // уже отсортирован по итоговой сумме. Менеджер сравнивает заводы по цене, а
      // нужную номенклатуру выберет сам нажатием на строку.
      //
      // Прежнее правило «предпочесть завод, у которого есть ровно выбранная
      // номенклатура» здесь мешало: выбрав из списка «Газобетон ЛСР …», менеджер
      // получал ЛСР за 260 500 ₽, хотя первым в таблице стоял ВКблок за 236 000 ₽.
      // Вне области правило остаётся прежним.
      cheapestEntry = dlyaVybora[0];
    } else if (gazobetonyProducts.length > 0) {
      // Для газобетона предпочитаем производства, у которых есть именно запрошенная номенклатура,
      // а не просто совпадение по характеристикам (размер/плотность)
      const normalize = (s) => String(s || '').trim().toLowerCase();
      const exactMatchEntries = dlyaVybora.filter(e =>
        gazobetonyProducts.every(gp =>
          (e.materialsToShow || []).some(
            m => isGazMaterial(m) && normalize(m.name) === normalize(gp.name)
          )
        )
      );
      cheapestEntry = exactMatchEntries.length > 0 ? exactMatchEntries[0] : dlyaVybora[0];
    } else {
      cheapestEntry = dlyaVybora[0];
    }

    if (cheapestEntry) {
      // ВАЖНО: раньше здесь же поддоны/факт-объём дешевейшего производства писались
      // в общий (для всех производств) products и сбрасывали _manualNumberPallets.
      // Из-за этого при каждом пересчёте comparisons (а он гоняется автоматически,
      // без действий пользователя) нормы одного — самого дешёвого на тот момент —
      // производства становились нормами вообще всех, и ручной выбор поддонов
      // («хочу ровно 60 поддонов, остальное — остатком») бесследно затирался.
      // Сам расчёт объёма для сравнения — per-production, см.
      // getGazRoundedPalletMetrics в BackendProductions.js — синк норм в products
      // ему не нужен, поэтому здесь остаётся только выбор офера для отображения.
      handleSelectOffer(cheapestEntry.comparison, cheapestEntry);
    } else if (!finalGroupedData.some(e => e.totalMaterialCostForDisplay > 0)) {
      // Ни одной строки с ценой — выбирать нечего. Прежнее предложение от прошлого
      // расчёта сбрасывается, иначе КП всплывало бы по устаревшим ценам.
      setSelectedOffer(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparisons]);

  const minDistanceKadToClient = getMinDistanceKadToClient(comparisons);

  return {
    isValidInput,
    groupedData: finalGroupedData,
    sortedGroupedData: finalGroupedData,
    hasValidDescriptions,
    minDistanceKadToClient,
    handleSelectOffer,
    selectedComparisonId,
    isLoading,
    setIsLoading,
  };
};

export default useProductionsLogic;
